/*
 * COPYRIGHT (c) 2023
 * HONEYWELL INC.,
 * ALL RIGHTS RESERVED
 *
 * This software is a copyrighted work and/or information protected as a trade secret.
 * Legal rights of Honeywell Inc. in this software is distinct from ownership of any medium
 * in which the software is embodied. Copyright or trade secret notices included must be
 * reproduced in any copies authorized by Honeywell Inc.
 *
 */

import {
  Accordion,
  Button,
  Card,
  Checkbox,
  Icon,
  Input,
  Popup
} from '@scuf/common';
import React, { useEffect, useState } from 'react';
import './AccountDropdown.scss';
import useFilter from '../SummaryTable/useFilter';
import { useTranslation } from '@scuf/localization';
import descriptors from '../../../../locales/en-US/descriptors';
import { IAccount } from '../../../DashboardKPIWidgets/IEnergyManagementProps';

interface IDropdownComponentProps {
  onApply?: (filteredData: any[]) => void;
  onReset?: () => void;
  data: any;
  tableLoader?: boolean;
  graphLoader?: boolean;
}

function AccountDropdown(props: IDropdownComponentProps) {
  const [t] = useTranslation();
  const d = descriptors.energyManagement;

  const [resetAll, setResetAll] = useState<boolean>(true);

  const {
    isOpen,
    data,
    onCheckboxClick,
    hidePopup,
    handleOpen,
    applyOnClickSelected,
    cancelOnClick,
    onSearch,
    onSelectAll
  } = useFilter(props);

  useEffect(() => {
    isOpen &&
      setResetAll(data.every((element: IAccount) => element.isSelected));
  }, [isOpen]);

  useEffect(() => {
    if (data.every((element: IAccount) => element.isSelected)) {
      setResetAll(true);
    } else {
      setResetAll(false);
    }
  }, [data]);

  const [searchText, setSearchText] = useState(false);
  return (
    <Popup
      element={<Icon root="common" name="filter" size="medium" />}
      hoverable={false}
      position="bottom right"
      on="click"
      open={isOpen}
      onOpen={handleOpen}
      onClose={hidePopup}
    >
      <Card className="filter-popup">
        <Card.Content className="popup-content">
          <Accordion>
            <Accordion.Content className="accordion" title="Accounts">
              <div className="content-list">
                {
                  <Input
                    key={1}
                    placeholder={t(d.SHOWACCOUNTNUMBERS)}
                    className="search-account"
                    onChange={(e) => {
                      e ? setSearchText(true) : setSearchText(false);
                      onSearch(e);
                    }}
                  />
                }
                {!searchText && (
                  <Checkbox
                    key={0}
                    label={t(d.SHOWACCOUNTNUMBERS)}
                    checked={resetAll}
                    onChange={(checked) => {
                      setResetAll(!resetAll);
                      onSelectAll(checked);
                    }}
                    disabled={data.length === 0}
                  />
                )}
                {Array.isArray(data) &&
                  data.map((element: any) => (
                    <Checkbox
                      key={element.accountNumber}
                      label={element.accountNumber}
                      checked={element.isSelected}
                      onChange={(checked) =>
                        onCheckboxClick(checked, element, 'accountNumber')
                      }
                      disabled={!searchText && data.length === 0}
                    />
                  ))}
              </div>
            </Accordion.Content>
          </Accordion>
        </Card.Content>
        <Card.Footer>
          <Button
            type="secondary"
            size="small"
            className="cancel-btn action-btn"
            onClick={() => {
              setSearchText(false);
              cancelOnClick();
            }}
            content={t(d.CANCEL)}
          />
          <Button
            disabled={
              !data.filter((item: any) => item.isSelected).length ||
              (!searchText && data.length === 0) ||
              props.tableLoader ||
              props.graphLoader
            }
            type="primary"
            size="small"
            className="apply-btn action-btn"
            onClick={() => {
              setSearchText(false);
              applyOnClickSelected();
            }}
            content={t(d.APPLY)}
          />
        </Card.Footer>
      </Card>
    </Popup>
  );
}

export default AccountDropdown;
